import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { RefreshCw, CheckCircle, Clock, ShieldOff, Shield, Search, Wrench, Car, UserCog, UserPlus, AlertTriangle, Eye, EyeOff } from "lucide-react";

function userState(u) {
  // Prefer explicit backend status field when present (new source of truth).
  if (u.status === "pending" || u.status === "approved" || u.status === "blocked") return u.status;
  // Legacy fallback — any non-admin user without isApproved sits in the approval queue
  if (u.isBlocked) return "blocked";
  if (u.role !== "admin" && !u.isApproved) return "pending";
  return "approved";
}

function isVerificationPending(u) {
  // Backend writes verificationStatus on register/admin-create. Legacy users
  // without the field are treated as pending if the required IDs are missing.
  if (u.verificationStatus === "verified") return false;
  if (u.verificationStatus === "pending_verification") return true;
  // Fallback for legacy users (created before this field existed)
  if (u.role === "mechanic") {
    return !(u.idNumber && u.driverLicenceNumber);
  }
  return !(u.idNumber || u.passportNumber);
}

const TABS = [
  { key: "mechanic", label: "Mechanics", icon: Wrench, color: "#3CFF7E" },
  { key: "client", label: "Clients", icon: Car, color: "#60A5FA" },
  { key: "admin", label: "Admins", icon: UserCog, color: "#E6FF3C" },
];

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [tab, setTab] = useState("mechanic");
  const [query, setQuery] = useState("");
  const [onlineOnly, setOnlineOnly] = useState(false);

  const load = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get("/admin/users");
      // Explicit debug log per requirement
      console.log(`[AdminUsers] fetched ${data.length} users from /api/admin/users`);
      console.log("[AdminUsers] breakdown:", {
        total: data.length,
        admin: data.filter((u) => u.role === "admin").length,
        mechanics: data.filter((u) => u.role === "mechanic").length,
        clients: data.filter((u) => u.role === "client").length,
      });
      setUsers(data);
    } catch (e) {
      if (!silent) toast.error(e.response?.data?.detail || "Failed to load users");
    } finally {
      if (!silent) setLoading(false);
    }
  };
  useEffect(() => {
    load();
    // Realtime: silently refresh on admin events from FCM (no scroll-to-top, no flicker)
    const onEvent = () => load({ silent: true });
    window.addEventListener("afr-admin-event", onEvent);
    return () => window.removeEventListener("afr-admin-event", onEvent);
  }, []);

  const counts = useMemo(() => ({
    mechanic: users.filter((u) => u.role === "mechanic").length,
    client: users.filter((u) => u.role === "client").length,
    admin: users.filter((u) => u.role === "admin").length,
    mechanicOnline: users.filter((u) => u.role === "mechanic" && u.availability && !u.isBlocked).length,
  }), [users]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return users
      .filter((u) => u.role === tab)
      .filter((u) => (tab === "mechanic" && onlineOnly) ? (u.availability && !u.isBlocked) : true)
      .filter((u) => !q
        || (u.name || "").toLowerCase().includes(q)
        || (u.email || "").toLowerCase().includes(q)
        || (u.phone || "").includes(q))
      .sort((a, b) => {
        // Pending first (only matters on the mechanics tab), then by name
        const sa = userState(a), sb = userState(b);
        if (sa !== sb) {
          if (sa === "pending") return -1;
          if (sb === "pending") return 1;
        }
        return (a.name || "").localeCompare(b.name || "");
      });
  }, [users, tab, query, onlineOnly]);

  const act = async (uid, apiCall, optimistic, label) => {
    setBusy(uid);
    // Optimistic UI: update the row immediately so the pill + button flip before the server replies
    setUsers((prev) => prev.map((u) => (u.id === uid ? { ...u, ...optimistic } : u)));
    try {
      const { data } = await apiCall();
      console.log(`[AdminUsers] ${label} →`, data);
      toast.success(label);
      await load({ silent: true }); // silent authoritative refetch
    } catch (e) {
      console.error(`[AdminUsers] ${label} FAILED →`, e.response?.data || e.message);
      toast.error(e.response?.data?.detail || "Failed");
      await load({ silent: true }); // revert to server truth — silently
    } finally {
      setBusy(null);
    }
  };

  const approve = (uid) => {
    console.log(`[AdminUsers] APPROVE clicked for uid=${uid}`);
    return act(
      uid,
      () => api.post(`/admin/mechanics/${uid}/approve`, { approved: true }),
      { status: "approved", isApproved: true },
      "Approved"
    );
  };
  const block = (uid) => {
    console.log(`[AdminUsers] BLOCK clicked for uid=${uid}`);
    return act(
      uid,
      () => api.post(`/admin/users/${uid}/block`, { blocked: true }),
      { status: "blocked", isBlocked: true, availability: false },
      "Blocked"
    );
  };
  const unblock = (uid) => {
    console.log(`[AdminUsers] UNBLOCK clicked for uid=${uid}`);
    return act(
      uid,
      () => api.post(`/admin/users/${uid}/block`, { blocked: false }),
      { status: "approved", isBlocked: false },
      "Unblocked"
    );
  };

  return (
    <div>
      <div className="px-6 pt-8 pb-4 flex items-end justify-between">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#3CFF7E] font-display mb-1">Admin</div>
          <h1 className="font-display text-3xl font-black tracking-tight">Users</h1>
          <p className="text-sm text-[#9CA3AF] mt-1" data-testid="admin-users-count">
            {loading ? "Loading…" : `${users.length} total · ${counts.mechanic} mechanics · ${counts.client} clients`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/app/admin/users/new"
            data-testid="admin-add-mechanic-btn"
            className="h-10 px-3 border border-[#3CFF7E]/40 bg-[#3CFF7E]/10 text-[#3CFF7E] rounded-md flex items-center gap-1 text-[10px] font-display font-bold uppercase tracking-wider active:scale-[0.98]"
          >
            <UserPlus size={14} /> Add
          </Link>
          <button
            onClick={() => load()}
            data-testid="admin-refresh-btn"
            className="h-10 w-10 border border-white/10 bg-[#111827] rounded-md flex items-center justify-center text-[#9CA3AF] hover:text-white hover:border-white/30"
            aria-label="Refresh"
          >
            <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      {/* Role tabs */}
      <div className="px-6">
        <div className="grid grid-cols-3 gap-2" role="tablist" data-testid="admin-users-tabs">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.key;
            const count = counts[t.key];
            return (
              <button
                key={t.key}
                role="tab"
                aria-selected={active}
                data-testid={`admin-tab-${t.key}`}
                onClick={() => setTab(t.key)}
                className={`border rounded-md p-3 text-left transition-all active:scale-[0.98] ${
                  active
                    ? "border-[#3CFF7E] bg-[#3CFF7E]/10"
                    : "border-white/10 bg-[#111827]"
                }`}
                style={active ? { color: t.color } : { color: "#9CA3AF" }}
              >
                <Icon size={18} />
                <div className="font-display font-bold text-xs uppercase tracking-wider mt-1">
                  {t.label}
                </div>
                <div className={`font-display font-black text-lg leading-none mt-1 ${active ? "text-white" : "text-white"}`}>
                  {loading ? "—" : count}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Search + (mechanic) online-only filter */}
      <div className="px-6 mt-4 space-y-2">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={`Search ${tab === "mechanic" ? "mechanics" : tab + "s"}…`}
            data-testid="admin-users-search"
            className="w-full h-11 pl-9 pr-3 border border-white/10 bg-[#111827] rounded-md text-sm focus:outline-none focus:border-[#3CFF7E]"
          />
        </div>
        {tab === "mechanic" && (
          <button
            type="button"
            onClick={() => setOnlineOnly((v) => !v)}
            aria-pressed={onlineOnly}
            data-testid="admin-online-only-toggle"
            className={`w-full h-10 rounded-md flex items-center justify-between px-3 text-[11px] font-display font-bold uppercase tracking-wider transition-colors ${
              onlineOnly
                ? "border border-[#3CFF7E] bg-[#3CFF7E]/10 text-[#3CFF7E]"
                : "border border-white/10 bg-[#111827] text-[#9CA3AF]"
            }`}
          >
            <span className="flex items-center gap-2">
              <span className={`w-2 h-2 rounded-full ${onlineOnly ? "bg-[#3CFF7E]" : "bg-[#9CA3AF]"}`} />
              Online only
            </span>
            <span className="text-[10px]">{counts.mechanicOnline} online · {counts.mechanic} total</span>
          </button>
        )}
      </div>

      {/* List */}
      <div className="px-6 py-4 space-y-2" data-testid={`admin-users-list-${tab}`}>
        {loading ? (
          <>
            <div className="skeleton h-20" />
            <div className="skeleton h-20" />
            <div className="skeleton h-20" />
          </>
        ) : visible.length === 0 ? (
          <div className="text-sm text-[#9CA3AF] border border-dashed border-white/10 rounded-md p-6 text-center">
            {query
              ? `No ${tab}s match "${query}".`
              : `No ${tab}s yet.`}
          </div>
        ) : (
          visible.map((u) => {
            const state = userState(u);
            const verifyPending = isVerificationPending(u);
            return (
              <div key={u.id} className="border border-white/10 bg-[#111827] rounded-md p-3" data-testid={`admin-user-${u.id}`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-display font-bold text-sm text-white truncate">{u.name}</div>
                    <div className="text-xs text-[#9CA3AF] truncate">{u.email}</div>
                    {u.phone && <div className="text-xs text-[#9CA3AF]">{u.phone}</div>}
                    {tab === "mechanic" && u.skills?.length > 0 && (
                      <div className="text-[10px] text-[#9CA3AF] mt-1 truncate">
                        {u.skills.slice(0, 4).join(" · ")}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    <StatePill state={state} />
                    {verifyPending && u.role !== "admin" && (
                      <span
                        data-testid={`pending-verification-${u.id}`}
                        className="inline-flex items-center gap-1 text-[9px] font-display font-bold uppercase tracking-[0.15em] text-[#FBBF24]"
                      >
                        <AlertTriangle size={10} /> Pending Verification
                      </span>
                    )}
                    {tab === "mechanic" && u.availability && state === "approved" && (
                      <span className="inline-flex items-center gap-1 text-[9px] font-display font-bold uppercase tracking-[0.15em] text-[#3CFF7E]">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#3CFF7E]" /> Online
                      </span>
                    )}
                  </div>
                </div>

                {u.role !== "admin" && (
                  <VerificationDetails user={u} />
                )}

                {u.role !== "admin" && (
                  <div className="mt-2 pt-2 border-t border-white/10 flex justify-end gap-2">
                    {state === "pending" && (
                      <button onClick={() => approve(u.id)} disabled={busy === u.id} data-testid={`approve-${u.id}`} className="h-9 px-3 bg-[#3CFF7E] text-[#0A0A0A] rounded-md text-[10px] font-display font-bold uppercase tracking-wider disabled:opacity-50">
                        {busy === u.id ? "..." : "Approve"}
                      </button>
                    )}
                    {state === "approved" && (
                      <button onClick={() => block(u.id)} disabled={busy === u.id} data-testid={`block-${u.id}`} className="h-9 px-3 border border-[#F87171]/50 text-[#F87171] rounded-md text-[10px] font-display font-bold uppercase tracking-wider disabled:opacity-50 flex items-center gap-1">
                        <ShieldOff size={12} /> {busy === u.id ? "..." : "Block"}
                      </button>
                    )}
                    {state === "blocked" && (
                      <button onClick={() => unblock(u.id)} disabled={busy === u.id} data-testid={`unblock-${u.id}`} className="h-9 px-3 bg-[#3CFF7E] text-[#0A0A0A] rounded-md text-[10px] font-display font-bold uppercase tracking-wider disabled:opacity-50 flex items-center gap-1">
                        <Shield size={12} /> {busy === u.id ? "..." : "Unblock"}
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

function StatePill({ state }) {
  const map = {
    pending: { icon: <Clock size={10} />, text: "text-[#E6FF3C]", label: "Pending" },
    approved: { icon: <CheckCircle size={10} />, text: "text-[#3CFF7E]", label: "Approved" },
    blocked: { icon: <ShieldOff size={10} />, text: "text-[#F87171]", label: "Blocked" },
  };
  const s = map[state];
  return (
    <span className={`inline-flex items-center gap-1 text-[9px] font-display font-bold uppercase tracking-[0.15em] ${s.text}`}>
      {s.icon} {s.label}
    </span>
  );
}

function VerificationDetails({ user }) {
  const [show, setShow] = useState(false);
  const hasAny = !!(
    user.idNumber || user.passportNumber || user.driverLicenceNumber || user.tradeQualificationNumber
  );
  if (!hasAny) {
    return (
      <div className="mt-2 pt-2 border-t border-white/5 text-[10px] text-[#9CA3AF] italic" data-testid={`verify-empty-${user.id}`}>
        No verification info on file.
      </div>
    );
  }
  return (
    <div className="mt-2 pt-2 border-t border-white/5">
      <button
        type="button"
        onClick={() => setShow((v) => !v)}
        data-testid={`verify-toggle-${user.id}`}
        className="inline-flex items-center gap-1 text-[10px] font-display font-bold uppercase tracking-[0.18em] text-[#9CA3AF] hover:text-white"
      >
        {show ? <EyeOff size={12} /> : <Eye size={12} />}
        {show ? "Hide" : "Show"} verification details
      </button>
      {show && (
        <div className="mt-2 space-y-1 text-[11px] text-[#D1D5DB]" data-testid={`verify-details-${user.id}`}>
          {user.idNumber && (
            <Row label="SA ID" value={user.idNumber} testid={`verify-id-${user.id}`} />
          )}
          {user.passportNumber && (
            <Row label="Passport" value={user.passportNumber} testid={`verify-passport-${user.id}`} />
          )}
          {user.driverLicenceNumber && (
            <Row label="Driver's licence" value={user.driverLicenceNumber} testid={`verify-licence-${user.id}`} />
          )}
          {user.tradeQualificationNumber && (
            <Row label="Trade qual." value={user.tradeQualificationNumber} testid={`verify-trade-${user.id}`} />
          )}
        </div>
      )}
    </div>
  );
}

function Row({ label, value, testid }) {
  return (
    <div className="flex items-center justify-between gap-3" data-testid={testid}>
      <span className="text-[10px] uppercase tracking-[0.15em] text-[#6B7280] font-display font-bold">
        {label}
      </span>
      <span className="font-mono text-white truncate">{value}</span>
    </div>
  );
}
